/**
 * SAAS-USD-BILLING-1 · Phase 1 — SaaS checkout-currency configuration modal.
 *
 * Stepped modal that captures the merchant's real checkout currency reality:
 *   1. Pick currencies (CAD, USD, or both)
 *   2. If both — pick primary
 *   3. If USD is enabled — show performance-fee disclosure + require the
 *      explicit `I confirm my checkout accepts USD` checkbox
 *   4. Confirmation summary → Confirm Currency Configuration
 *
 * Server rules (mirrored here for UX; backend is authoritative):
 *   * Non-empty currency selection.
 *   * `primary_checkout_currency` must be one of the picked currencies.
 *   * USD only permitted when `usd_checkout_confirmed=true` is included.
 *   * `usd_checkout_enabled` is NEVER sent — the server derives it.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, DollarSign, CircleDollarSign, Check, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const CURRENCY_META = {
  CAD: { label: "CAD", description: "Canadian Dollar" },
  USD: { label: "USD", description: "US Dollar" },
};

const STEP_PICK = "pick";
const STEP_PRIMARY = "primary";
const STEP_USD_DISCLOSURE = "usd_disclosure";
const STEP_CONFIRM = "confirm";

export default function SaasCheckoutCurrencyModal({
  open,
  onClose,
  initial, // { supported_checkout_currencies, primary_checkout_currency, usd_checkout_enabled }
  onSaved,
}) {
  const [step, setStep] = useState(STEP_PICK);
  const [cadOn, setCadOn] = useState(true);
  const [usdOn, setUsdOn] = useState(false);
  const [primary, setPrimary] = useState("CAD");
  const [confirmedUsd, setConfirmedUsd] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Seed from current state (or defaults for first-time configuration).
    // Depend on stable primitives (not the `initial` object) to avoid the
    // modal state resetting every time the parent re-renders.
    const supported = new Set(initial?.supported_checkout_currencies || []);
    setCadOn(supported.size ? supported.has("CAD") : true);
    setUsdOn(supported.has("USD"));
    setPrimary(initial?.primary_checkout_currency || (supported.has("CAD") ? "CAD" : "USD") || "CAD");
    setConfirmedUsd(false); // Always require fresh confirmation on every USD-enabled save.
    setStep(STEP_PICK);

    const onKeyDown = (e) => {
      if (e.key === "Escape" && !busy) onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, busy]);

  if (!open) return null;

  const selected = [cadOn && "CAD", usdOn && "USD"].filter(Boolean);
  const both = cadOn && usdOn;
  const enablingUsd = usdOn;

  function goNextFromPick() {
    if (selected.length === 0) return;
    // Auto-set primary if only one currency is selected.
    if (selected.length === 1) {
      setPrimary(selected[0]);
    } else if (!selected.includes(primary)) {
      setPrimary(selected[0]);
    }
    // Route to correct next step.
    if (both) return setStep(STEP_PRIMARY);
    if (enablingUsd) return setStep(STEP_USD_DISCLOSURE);
    return setStep(STEP_CONFIRM);
  }

  function goNextFromPrimary() {
    if (enablingUsd) return setStep(STEP_USD_DISCLOSURE);
    setStep(STEP_CONFIRM);
  }

  function goNextFromDisclosure() {
    if (!confirmedUsd) return;
    setStep(STEP_CONFIRM);
  }

  async function commit() {
    setBusy(true);
    try {
      const payload = {
        supported_checkout_currencies: selected,
        primary_checkout_currency: primary,
      };
      if (enablingUsd) payload.usd_checkout_confirmed = true;
      const r = await api.put("/merchant/store", payload);
      toast.success("Checkout currency configuration saved");
      onSaved?.(r.data);
      onClose?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : d?.message || "Could not save configuration. Please try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] overflow-y-auto bg-black/60 backdrop-blur-sm p-4 sm:p-6 flex items-start sm:items-center justify-center min-h-screen"
      data-testid="saas-currency-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="fixed inset-0 bg-transparent"
        onClick={busy ? undefined : onClose}
        aria-hidden="true"
      />
      <div
        className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl relative z-10 my-auto max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          disabled={busy}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100 text-slate-500 disabled:opacity-40 z-20"
          data-testid="saas-currency-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {step === STEP_PICK && (
          <div className="p-6 sm:p-8 overflow-y-auto" data-testid="saas-currency-step-pick">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-cyan-700">
              Checkout currencies
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900 mt-2">
              What currencies does your checkout accept?
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              Pick every currency that your live checkout can actually charge in. This tells
              Cyannect which transaction currencies to accept for your SaaS conversions.
            </p>

            <div className="grid md:grid-cols-2 gap-3 mt-6">
              <CurrencyToggle
                code="CAD"
                on={cadOn}
                onChange={setCadOn}
                data-testid="saas-currency-toggle-cad"
              />
              <CurrencyToggle
                code="USD"
                on={usdOn}
                onChange={setUsdOn}
                data-testid="saas-currency-toggle-usd"
              />
            </div>

            {selected.length === 0 && (
              <p className="mt-3 text-xs text-amber-700" data-testid="saas-currency-empty-warn">
                Select at least one currency.
              </p>
            )}

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-full text-sm font-semibold border border-slate-200 hover:bg-slate-50"
                data-testid="saas-currency-cancel"
              >
                Cancel
              </button>
              <button
                onClick={goNextFromPick}
                disabled={selected.length === 0}
                className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="saas-currency-next"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === STEP_PRIMARY && (
          <div className="p-6 sm:p-8 overflow-y-auto" data-testid="saas-currency-step-primary">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-cyan-700">
              Primary currency
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900 mt-2">
              Which is your primary checkout currency?
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              This is the currency Cyannect will use by default when generating checkout links or
              settling in your ledgers. You can still accept the other currency alongside it.
            </p>

            <div className="grid md:grid-cols-2 gap-3 mt-6">
              {selected.map((code) => (
                <label
                  key={code}
                  className={`p-4 rounded-xl border cursor-pointer transition-all ${
                    primary === code
                      ? "border-cyan-500 ring-2 ring-cyan-200 bg-cyan-50/50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`saas-currency-primary-${code.toLowerCase()}`}
                >
                  <input
                    type="radio"
                    name="primary"
                    className="sr-only"
                    checked={primary === code}
                    onChange={() => setPrimary(code)}
                  />
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-8 h-8 rounded-full inline-flex items-center justify-center ${
                        primary === code
                          ? "bg-cyan-600 text-white"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {code === "USD" ? (
                        <CircleDollarSign className="w-4 h-4" />
                      ) : (
                        <DollarSign className="w-4 h-4" />
                      )}
                    </span>
                    <div>
                      <div className="font-display text-base font-medium tracking-tight text-slate-900">
                        {code}
                      </div>
                      <div className="text-xs text-slate-500">
                        {CURRENCY_META[code]?.description}
                      </div>
                    </div>
                    {primary === code && <Check className="w-4 h-4 text-cyan-600 ml-auto" />}
                  </div>
                </label>
              ))}
            </div>

            <div className="mt-6 flex justify-between gap-2">
              <button
                onClick={() => setStep(STEP_PICK)}
                className="px-4 py-2 rounded-full text-sm font-semibold border border-slate-200 hover:bg-slate-50"
                data-testid="saas-currency-primary-back"
              >
                Back
              </button>
              <button
                onClick={goNextFromPrimary}
                className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold"
                data-testid="saas-currency-primary-next"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === STEP_USD_DISCLOSURE && (
          <div className="p-6 sm:p-8 overflow-y-auto" data-testid="saas-currency-step-usd-disclosure">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-700 inline-flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> USD checkout disclosure
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900 mt-2">
              Please confirm your checkout accepts USD
            </h3>
            <div className="mt-4 rounded-xl bg-amber-50/80 border border-amber-200 p-4 text-sm text-slate-800 leading-relaxed">
              <p className="font-semibold">What this means</p>
              <ul className="list-disc pl-5 mt-2 space-y-1 text-black">
                <li>
                  Cyannect will accept SaaS conversion events from your checkout in USD and
                  calculate performance fees in USD, without converting to CAD.
                </li>
                <li>
                  You are responsible for actually charging shoppers in USD on your checkout. Do
                  not enable USD if your storefront still charges in CAD only.
                </li>
                <li>
                  This setting affects invoicing and reporting once Phase 2 (USD fee engine) is
                  activated. Fee calculation itself is unchanged today.
                </li>
              </ul>
            </div>

            <label className="mt-5 flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmedUsd}
                onChange={(e) => setConfirmedUsd(e.target.checked)}
                className="mt-1 w-4 h-4 rounded"
                data-testid="saas-currency-usd-confirm"
              />
              <span className="text-sm text-slate-800">
                I confirm my checkout accepts USD and I agree to Cyannect calculating USD
                performance fees on USD transactions.
              </span>
            </label>

            <div className="mt-6 flex justify-between gap-2">
              <button
                onClick={() => setStep(both ? STEP_PRIMARY : STEP_PICK)}
                className="px-4 py-2 rounded-full text-sm font-semibold border border-slate-200 hover:bg-slate-50"
                data-testid="saas-currency-usd-back"
              >
                Back
              </button>
              <button
                onClick={goNextFromDisclosure}
                disabled={!confirmedUsd}
                className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="saas-currency-usd-next"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === STEP_CONFIRM && (
          <div className="p-6 sm:p-8 overflow-y-auto" data-testid="saas-currency-step-confirm">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-cyan-700">
              Confirm your configuration
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900 mt-2">
              Save these checkout currencies?
            </h3>
            <dl className="mt-5 space-y-3 rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-xs uppercase tracking-widest text-slate-500">
                  Supported currencies
                </dt>
                <dd className="text-sm font-mono font-semibold text-slate-900" data-testid="saas-currency-summary-supported">
                  {selected.join(", ")}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-xs uppercase tracking-widest text-slate-500">
                  Primary currency
                </dt>
                <dd className="text-sm font-mono font-semibold text-slate-900" data-testid="saas-currency-summary-primary">
                  {primary}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-xs uppercase tracking-widest text-slate-500">
                  USD checkout
                </dt>
                <dd className="text-sm font-semibold text-slate-900" data-testid="saas-currency-summary-usd">
                  {enablingUsd ? "Enabled" : "Disabled"}
                </dd>
              </div>
            </dl>

            <div className="mt-6 flex justify-between items-center gap-2">
              <button
                onClick={() =>
                  setStep(enablingUsd ? STEP_USD_DISCLOSURE : both ? STEP_PRIMARY : STEP_PICK)
                }
                disabled={busy}
                className="px-4 py-2 rounded-full text-sm font-semibold border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
                data-testid="saas-currency-confirm-back"
              >
                Back
              </button>
              <button
                onClick={commit}
                disabled={busy}
                className="pf-btn-primary !text-black font-bold px-5 py-2.5 rounded-full text-sm disabled:opacity-40"
                data-testid="saas-currency-confirm-btn"
              >
                {busy ? "Saving…" : "Confirm Currency Configuration"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function CurrencyToggle({ code, on, onChange, ...rest }) {
  return (
    <label
      className={`p-4 rounded-xl border cursor-pointer transition-all ${
        on ? "border-cyan-500 ring-2 ring-cyan-200 bg-cyan-50/50" : "border-slate-200 hover:border-slate-300"
      }`}
      {...rest}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={on}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div className="flex items-center gap-2">
        <span
          className={`w-8 h-8 rounded-full inline-flex items-center justify-center ${
            on ? "bg-cyan-600 text-white" : "bg-slate-100 text-slate-600"
          }`}
        >
          {code === "USD" ? <CircleDollarSign className="w-4 h-4" /> : <DollarSign className="w-4 h-4" />}
        </span>
        <div>
          <div className="font-display text-base font-medium tracking-tight text-slate-900">
            {code}
          </div>
          <div className="text-xs text-slate-500">{CURRENCY_META[code]?.description}</div>
        </div>
        {on && <Check className="w-4 h-4 text-cyan-600 ml-auto" />}
      </div>
    </label>
  );
}
